/**
 * useResearchEvents Hook
 * Fetches and manages research events from the API with filtering
 * 
 * Author: Coach Cedar Development Team
 * Date: October 23, 2025
 */

import { useState, useEffect, useCallback } from 'react';
import { storage } from '../services/storage';

export type ResearchEventFilter = 'recent' | 'move' | 'chat';

interface ResearchSource {
  id: string;
  title: string;
  authors: string | string[];
  journal?: string;
  publication_year?: number;
  doi?: string;
  evidence_strength?: string;
  key_findings?: string[];
  // 🆕 DEC 2025: Primary source fields (from conversation_sources_used)
  application_summary?: string;
  key_influence?: string;
  cited_finding?: string;
  applied_to?: string;
}

// 🆕 DEC 2025: Enriched framework interface
interface EnrichedFramework {
  id: string;
  framework_name: string;
  description: string;
  core_principles?: string[];
  goal_contexts?: string[];
  resource_id: string;
  resource_name: string;
  expertise_areas?: string[];
  consensus_alignment_score?: number;
}

interface ResearchEvent {
  id: string;
  user_id: string;
  event_type: string;
  event_title: string;
  event_description: string;
  event_date: string;
  event_date_friendly?: string;
  
  // Context references
  goal_id?: string;
  plan_id?: string;
  workout_id?: string;
  framework_id?: string;
  insight_id?: string;
  
  // Intelligence-specific
  intelligence_engine?: string;
  intelligence_engine_label?: string;
  intelligence_subcategory?: string;
  user_query?: string;
  analysis_summary?: string[];
  
  // Research sources
  research_sources: string[];
  source_count: number;
  
  // 🆕 JAN 2025: Enriched sources from backend (full source objects with metadata)
  enriched_sources?: ResearchSource[];
  
  // 🆕 DEC 2025: Two-tier source separation
  primary_sources?: ResearchSource[];
  supporting_sources?: ResearchSource[];
  
  // 🆕 DEC 2025: Framework and principle tracking
  resource_frameworks?: string[];
  framework_count?: number;
  consensus_principles?: string[];
  principle_count?: number;
  enriched_frameworks?: EnrichedFramework[];
}

interface ResearchEventsData {
  events: ResearchEvent[];
  total_count: number;
  filter_type: string;
  filters?: {
    engine?: string;
    subcategory?: string;
  };
}

interface UseResearchEventsOptions {
  initialFilter?: ResearchEventFilter;
  engine?: string;
  subcategory?: string;
  limit?: number;
  autoLoad?: boolean;
}

interface UseResearchEventsResult {
  data: ResearchEventsData | null;
  loading: boolean;
  error: string | null;
  filter: ResearchEventFilter;
  engine: string | undefined;
  subcategory: string | undefined;
  setFilter: (filter: ResearchEventFilter) => void;
  setEngine: (engine: string | undefined) => void;
  setSubcategory: (subcategory: string | undefined) => void;
  refresh: () => Promise<void>;
}

export function useResearchEvents(
  options: UseResearchEventsOptions = {}
): UseResearchEventsResult {
  const {
    initialFilter = 'recent',
    engine: initialEngine,
    subcategory: initialSubcategory,
    limit = 50,
    autoLoad = true
  } = options;

  const [data, setData] = useState<ResearchEventsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ResearchEventFilter>(initialFilter);
  const [engine, setEngine] = useState<string | undefined>(initialEngine);
  const [subcategory, setSubcategory] = useState<string | undefined>(initialSubcategory);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('filter', filter);
      params.append('limit', limit.toString());
      
      if (engine) {
        params.append('engine', engine);
      }
      if (subcategory) {
        params.append('subcategory', subcategory);
      }

      // Use relative URL (same as useResearchLibrary) to avoid CORS/auth issues
      const token = await storage.getItem('jwt_token');
      
      const response = await fetch(`/api/research/events?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setData(data);
    } catch (err: any) {
      console.error('Error fetching research events:', err);
      setError(err.message || 'Failed to load research events');
    } finally {
      setLoading(false);
    }
  }, [filter, engine, subcategory, limit]);

  // Auto-load on mount or when dependencies change
  useEffect(() => {
    if (autoLoad) {
      fetchEvents();
    }
  }, [fetchEvents, autoLoad]);

  return {
    data,
    loading,
    error,
    filter,
    engine,
    subcategory,
    setFilter,
    setEngine,
    setSubcategory,
    refresh: fetchEvents
  };
}

// Hook for fetching event detail with sources
export function useResearchEventDetail(eventId: string | null) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setData(null);
      return;
    }

    const fetchEventDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use relative URL to avoid CORS/auth issues
        const token = await storage.getItem('jwt_token');
        
        const response = await fetch(`/api/research/events/${eventId}/detail`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setData(data);
      } catch (err: any) {
        console.error('Error fetching event detail:', err);
        setError(err.message || 'Failed to load event details');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetail();
  }, [eventId]);

  return { data, loading, error };
}

